import { useMutation } from '@apollo/client';
import EditActionButton from '@components/EditActionButton';
import { useSnackbar } from '@components/Snackbar/SnackbarProvider';
import { GET_PRO, UPDATE_SOCIAL_PRO } from '@gql/pros';
import { Box, Stack, Typography, useTheme } from '@mui/material';
import { useState } from 'react';
import { SectionCard } from '../../talents/components/SectionCard';
import { SectionTitle } from '../../talents/components/SectionTitle';
import CompanyContactsForm from './CompanyContactsForm';

type ContactItem = {
    icon: string;
    text: string;
};

interface CompanyContactsCardProps {
    pro: any;
    onUpdated?: () => void;
}

const CompanyContactsCard = ({ pro, onUpdated }: CompanyContactsCardProps) => {
    const theme = useTheme();
    const { showSnackbar } = useSnackbar();
    const [isEditMode, setIsEditMode] = useState(false);

    const [updateSocial, { loading: loadingSocial }] = useMutation(UPDATE_SOCIAL_PRO);
    const loading = loadingSocial;

    const handleSubmit = async values => {
        const refetchQueries = [{ query: GET_PRO, variables: { proId: pro?._id } }];
        try {
            updateSocial({
                variables: {
                    proId: pro?._id,
                    proInput: values.social,
                },
                refetchQueries,
            })

            showSnackbar('Coordonnées mises à jour avec succès', 'success');
            setIsEditMode(false);
            onUpdated?.();
        } catch (_error) {
            showSnackbar('Erreur lors de la mise à jour des coordonnées', 'error');
        }
    };

    const contacts: ContactItem[] = [
        pro.social?.website && { icon: 'fi-br-browser', text: pro.social.website },
        pro.social?.instagram && { icon: 'fi-brands-instagram', text: pro.social.instagram },
        pro.social?.facebook && { icon: 'fi-brands-facebook', text: pro.social.facebook },
        pro.social?.linkedin && { icon: 'fi-brands-linkedin', text: pro.social.linkedin },
    ].filter((contact): contact is ContactItem => Boolean(contact));

    return (
        <SectionCard sx={{ display: 'flex', flexDirection: 'column', gap: '12px', padding: 0, overflow: 'hidden', background: theme.palette.dashboard.main }}>
            <Box
                sx={{
                    paddingY: 0,
                    overflow: 'hidden',
                    flex: 1,
                }}>
                <Box
                    sx={{
                        padding: '12px 16px 0 16px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                    }}>
                    <SectionTitle title="Coordonnées" />

                    {!isEditMode && (
                        <EditActionButton onClick={() => setIsEditMode(true)} />
                    )}
                </Box>

                {isEditMode ? (
                    <Box sx={{ padding: 2 }}>
                        <CompanyContactsForm
                            pro={pro}
                            loading={loading}
                            onCancel={() => setIsEditMode(false)}
                            onSubmit={handleSubmit}
                        />
                    </Box>
                ) : (
                    <Stack sx={{ padding: 2 }} gap={1}>
                        {contacts.map((contact, index) => (
                            <Box
                                key={`${contact.icon}-${index}`}
                                sx={{
                                    display: 'flex',
                                    gap: '8px',
                                    alignItems: 'center',
                                }}>
                                <i
                                    className={`fi ${contact.icon}`}
                                    style={{ fontSize: '16px', color: theme.palette.neutralWhite.main }}
                                />
                                <Typography variant="body2" fontSize="13px" fontWeight={500}>
                                    {contact.text}
                                </Typography>
                            </Box>
                        ))}
                    </Stack>
                )}
            </Box>
        </SectionCard>
    );
};

export default CompanyContactsCard;
